import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AuthService } from '../services/auth'
import VideoThumbnail from './VideoThumbnail'
import { useCoalescedWrite } from '../hooks/useCoalescedWrite'

interface FeaturedVideoGroup {
  id: string
  name: string
  description: string | null
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
  created_by: string | null
}

interface FeaturedVideo {
  id: string
  group_id: string
  video_id: string
  display_order: number
  added_at: string
  added_by: string | null
  video?: {
    id: string
    title: string
    thumbnail_url: string | null
    duration: string
    vimeo_id: string
  }
}

interface Video {
  id: string
  title: string
  thumbnail_url: string | null
  duration: string
  vimeo_id: string
}

const FeaturedVideoManager = () => {
  const [groups, setGroups] = useState<FeaturedVideoGroup[]>([])
  const [selectedGroup, setSelectedGroup] = useState<FeaturedVideoGroup | null>(null)
  const [featuredVideos, setFeaturedVideos] = useState<FeaturedVideo[]>([])
  const [availableVideos, setAvailableVideos] = useState<Video[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddGroup, setShowAddGroup] = useState(false)
  const [showAddVideos, setShowAddVideos] = useState(false)
  const [selectedVideos, setSelectedVideos] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [editingGroup, setEditingGroup] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [draggedGroup, setDraggedGroup] = useState<string | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [draggedVideo, setDraggedVideo] = useState<string | null>(null)
  const [dragOverVideoIndex, setDragOverVideoIndex] = useState<number | null>(null)
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    display_order: 0,
    is_active: true
  })
  const queueGroupOrder = useCoalescedWrite<FeaturedVideoGroup[]>()
  const queueVideoOrder = useCoalescedWrite<FeaturedVideo[]>()

  useEffect(() => {
    loadGroups()
    loadAvailableVideos()
  }, [])

  useEffect(() => {
    if (selectedGroup) {
      loadFeaturedVideos(selectedGroup.id)
    }
  }, [selectedGroup])

  const loadGroups = async () => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase
        .from('featured_video_groups')
        .select('*')
        .is('school_id', null)
        .order('display_order', { ascending: true })

      if (error) throw error
      setGroups(data || [])
    } catch (error) {
      console.error('Failed to load featured video groups:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadFeaturedVideos = async (groupId: string) => {
    try {
      const { data, error } = await supabase
        .from('featured_videos')
        .select(`
          *,
          video:videos (
            id,
            title,
            thumbnail_url,
            duration,
            vimeo_id
          )
        `)
        .eq('group_id', groupId)
        .order('display_order', { ascending: true })

      if (error) throw error
      setFeaturedVideos(data || [])
    } catch (error) {
      console.error('Failed to load featured videos:', error)
    }
  }

  const loadAvailableVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('id, title, thumbnail_url, duration, vimeo_id')
        .is('school_id', null)
        .order('created_at', { ascending: false })

      if (error) throw error
      setAvailableVideos(data || [])
    } catch (error) {
      console.error('Failed to load available videos:', error)
    }
  }

  const handleCreateGroup = async () => {
    try {
      const currentUser = await AuthService.getCurrentUser()
      if (!currentUser) return

      const { data, error } = await supabase
        .from('featured_video_groups')
        .insert([{
          ...groupForm,
          school_id: null,
          created_by: currentUser.id
        }])
        .select()

      if (error) throw error

      await AuthService.logAdminAction('create_featured_group', 'featured_video_groups', data?.[0]?.id, {
        group_name: groupForm.name
      })

      await loadGroups()
      setShowAddGroup(false)
      setGroupForm({ name: '', description: '', display_order: 0, is_active: true })
    } catch (error) {
      console.error('Failed to create group:', error)
      alert('グループの作成に失敗しました')
    }
  }

  const handleUpdateGroup = async (group: FeaturedVideoGroup) => {
    try {
      const { error } = await supabase
        .from('featured_video_groups')
        .update({
          name: group.name,
          description: group.description,
          display_order: group.display_order,
          is_active: group.is_active
        })
        .eq('id', group.id)

      if (error) throw error

      await AuthService.logAdminAction('update_featured_group', 'featured_video_groups', group.id, {
        group_name: group.name
      })

      await loadGroups()
      if (selectedGroup?.id === group.id) {
        setSelectedGroup({ ...selectedGroup, ...group })
      }
    } catch (error) {
      console.error('Failed to update group:', error)
      alert('グループの更新に失敗しました')
    }
  }

  const handleToggleGroupStatus = async (group: FeaturedVideoGroup) => {
    const updatedGroup = { ...group, is_active: !group.is_active }
    await handleUpdateGroup(updatedGroup)
  }

  const handleUpdateGroupName = async (groupId: string, newName: string) => {
    if (!newName.trim()) return

    const group = groups.find(g => g.id === groupId)
    if (!group) return

    const updatedGroup = { ...group, name: newName.trim() }
    await handleUpdateGroup(updatedGroup)
    setEditingGroup(null)
    setEditingName('')
  }

  const persistGroupOrder = (newGroups: FeaturedVideoGroup[]) => {
    // Optimistic update so the list snaps to its final order immediately
    setGroups(newGroups)

    // Queued so quick successive drags can't race each other on the server.
    return queueGroupOrder(newGroups, async (ordered) => {
      try {
        // One request, one transaction: every group is renumbered or none is.
        // p_school_id is null here — this manager only owns the system-wide groups.
        const { error } = await supabase.rpc('reorder_featured_video_groups', {
          p_school_id: null,
          p_group_ids: ordered.map(group => group.id)
        })

        if (error) throw error
        // No reload on success: the optimistic update above already reflects the
        // final order, and loadGroups() would flash the loading state.
      } catch (error) {
        console.error('Failed to reorder groups:', error)
        alert('グループの並び替えに失敗しました')
        await loadGroups()
      }
    })
  }

  const handleMoveGroup = (groupId: string, direction: 'up' | 'down') => {
    const currentIndex = groups.findIndex(g => g.id === groupId)
    if (currentIndex === -1) return

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= groups.length) return

    const newGroups = [...groups]
    const [moved] = newGroups.splice(currentIndex, 1)
    newGroups.splice(newIndex, 0, moved)

    persistGroupOrder(newGroups)
  }

  // insertIndex is the drop position in the current `groups` array
  // (0 = before the first group, groups.length = after the last group).
  const handleGroupDrop = (insertIndex: number) => {
    const draggedId = draggedGroup
    setDraggedGroup(null)
    setDragOverIndex(null)

    if (!draggedId) return

    const draggedIndex = groups.findIndex(g => g.id === draggedId)
    if (draggedIndex === -1) return

    // Removing the dragged item shifts everything after it left by one,
    // so adjust the insertion point accordingly.
    let target = insertIndex
    if (draggedIndex < target) target--

    if (target === draggedIndex) return

    const newGroups = [...groups]
    const [dragged] = newGroups.splice(draggedIndex, 1)
    newGroups.splice(target, 0, dragged)

    persistGroupOrder(newGroups)
  }

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('このグループを削除してもよろしいですか？')) return

    try {
      const { error } = await supabase
        .from('featured_video_groups')
        .delete()
        .eq('id', groupId)

      if (error) throw error

      await AuthService.logAdminAction('delete_featured_group', 'featured_video_groups', groupId)
      await loadGroups()
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null)
        setFeaturedVideos([])
      }
    } catch (error) {
      console.error('Failed to delete group:', error)
      alert('グループの削除に失敗しました')
    }
  }

  const handleAddVideos = async () => {
    if (!selectedGroup || selectedVideos.length === 0) return

    try {
      const currentUser = await AuthService.getCurrentUser()
      if (!currentUser) return

      const maxOrder = Math.max(...featuredVideos.map(v => v.display_order), -1)
      
      const videosToAdd = selectedVideos.map((videoId, index) => ({
        group_id: selectedGroup.id,
        video_id: videoId,
        display_order: maxOrder + index + 1,
        added_by: currentUser.id
      }))

      const { error } = await supabase
        .from('featured_videos')
        .insert(videosToAdd)

      if (error) throw error

      await AuthService.logAdminAction('add_featured_videos', 'featured_videos', selectedGroup.id, {
        video_count: selectedVideos.length,
        group_name: selectedGroup.name
      })

      await loadFeaturedVideos(selectedGroup.id)
      setShowAddVideos(false)
      setSelectedVideos([])
      setSearchTerm('')
    } catch (error) {
      console.error('Failed to add videos:', error)
      alert('動画の追加に失敗しました')
    }
  }

  const handleRemoveVideo = async (featuredVideoId: string) => {
    if (!confirm('この動画を削除してもよろしいですか？')) return

    try {
      const { error } = await supabase
        .from('featured_videos')
        .delete()
        .eq('id', featuredVideoId)

      if (error) throw error

      if (selectedGroup) {
        await loadFeaturedVideos(selectedGroup.id)
      }
    } catch (error) {
      console.error('Failed to remove video:', error)
      alert('動画の削除に失敗しました')
    }
  }

  const persistVideoOrder = (newVideos: FeaturedVideo[]) => {
    // The reorder controls only render inside the selected-group branch.
    if (!selectedGroup) return

    // Optimistic update so the list settles into its final order without flicker
    setFeaturedVideos(newVideos)

    const groupId = selectedGroup.id

    // Queued so quick successive drags can't race each other on the server.
    return queueVideoOrder(newVideos, async (ordered) => {
      try {
        // One request, one transaction: every video is renumbered or none is.
        const { error } = await supabase.rpc('reorder_featured_videos', {
          p_group_id: groupId,
          p_video_ids: ordered.map(video => video.id)
        })

        if (error) throw error
        // No reload on success: the optimistic update already reflects the order.
      } catch (error) {
        console.error('Failed to reorder videos:', error)
        alert('動画の並び替えに失敗しました')
        await loadFeaturedVideos(groupId)
      }
    })
  }

  const handleReorderVideos = (videoId: string, direction: 'up' | 'down') => {
    const currentIndex = featuredVideos.findIndex(v => v.id === videoId)
    if (currentIndex === -1) return

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= featuredVideos.length) return

    const newVideos = [...featuredVideos]
    const [moved] = newVideos.splice(currentIndex, 1)
    newVideos.splice(newIndex, 0, moved)

    persistVideoOrder(newVideos)
  }

  // insertIndex is the drop position in the current `featuredVideos` array
  // (0 = before the first video, featuredVideos.length = after the last video).
  const handleVideoDrop = (insertIndex: number) => {
    const draggedId = draggedVideo
    setDraggedVideo(null)
    setDragOverVideoIndex(null)

    if (!draggedId) return

    const draggedIndex = featuredVideos.findIndex(v => v.id === draggedId)
    if (draggedIndex === -1) return

    let target = insertIndex
    if (draggedIndex < target) target--

    if (target === draggedIndex) return

    const newVideos = [...featuredVideos]
    const [dragged] = newVideos.splice(draggedIndex, 1)
    newVideos.splice(target, 0, dragged)

    persistVideoOrder(newVideos)
  }

  const filteredAvailableVideos = availableVideos.filter(video => {
    const isNotInGroup = !featuredVideos.some(fv => fv.video_id === video.id)
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase())
    return isNotInGroup && matchesSearch
  })

  if (isLoading) {
    return <div className="p-4">読み込み中...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">特集グループ管理</h2>
        <button
          onClick={() => setShowAddGroup(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          新規グループ作成
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Groups List */}
        <div className="md:col-span-1">
          <div
            className="bg-gray-50 rounded-lg p-4"
            onDragOver={(e) => {
              if (!draggedGroup) return
              // The whole panel is one drop zone: always allow the drop so the
              // cursor's "drop" icon shows everywhere — including above the first
              // card and below the last card (the natural way to target the ends).
              e.preventDefault()
              // Derive the insertion point from the pointer position relative
              // to each card's vertical midpoint.
              const items = Array.from(
                e.currentTarget.querySelectorAll<HTMLElement>('[data-group-item]')
              )
              let insertIndex = items.length
              for (let i = 0; i < items.length; i++) {
                const rect = items[i].getBoundingClientRect()
                if (e.clientY < rect.top + rect.height / 2) {
                  insertIndex = i
                  break
                }
              }
              setDragOverIndex(insertIndex)
            }}
            onDrop={(e) => {
              e.preventDefault()
              if (dragOverIndex !== null) {
                handleGroupDrop(dragOverIndex)
              } else {
                setDraggedGroup(null)
                setDragOverIndex(null)
              }
            }}
          >
            <h3 className="text-sm font-medium text-gray-700 mb-3">グループ一覧</h3>
            <div className="space-y-2">
              {groups.map((group, index) => (
                <div
                  key={group.id}
                  data-group-item
                  draggable
                  onDragStart={() => setDraggedGroup(group.id)}
                  onDragEnd={() => {
                    setDraggedGroup(null)
                    setDragOverIndex(null)
                  }}
                  className={`p-3 rounded-md transition-colors relative ${
                    selectedGroup?.id === group.id
                      ? 'bg-blue-100 border-blue-500 border'
                      : 'bg-white hover:bg-gray-50 border border-gray-200'
                  } ${draggedGroup === group.id ? 'opacity-50' : ''}`}
                >
                  {/* Drop-position indicator: shows where the group will land.
                      pointer-events-none so it never intercepts drag/drop. */}
                  {draggedGroup && dragOverIndex === index && (
                    <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                  )}
                  {draggedGroup && index === groups.length - 1 && dragOverIndex === groups.length && (
                    <div className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex-grow" onClick={() => setSelectedGroup(group)}>
                      {editingGroup === group.id ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => handleUpdateGroupName(group.id, editingName)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateGroupName(group.id, editingName)
                            } else if (e.key === 'Escape') {
                              setEditingGroup(null)
                              setEditingName('')
                            }
                          }}
                          className="w-full text-sm font-medium bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <h4
                          className="font-medium text-sm cursor-pointer hover:text-blue-600"
                          onDoubleClick={(e) => {
                            e.stopPropagation()
                            setEditingGroup(group.id)
                            setEditingName(group.name)
                          }}
                        >
                          {group.name}
                        </h4>
                      )}
                      {group.description && (
                        <p className="text-xs text-gray-500 mt-1">{group.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleGroupStatus(group)
                        }}
                        className={`px-2 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                          group.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {group.is_active ? '有効' : '無効'}
                      </button>

                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMoveGroup(group.id, 'up')
                          }}
                          disabled={index === 0}
                          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          title="上へ移動"
                        >
                          ↑
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMoveGroup(group.id, 'down')
                          }}
                          disabled={index === groups.length - 1}
                          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          title="下へ移動"
                        >
                          ↓
                        </button>
                      </div>

                      <div className="flex flex-col gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingGroup(group.id)
                            setEditingName(group.name)
                          }}
                          className="text-gray-400 hover:text-gray-600 text-xs"
                          title="名前を編集"
                        >
                          ✏️
                        </button>
                        <button
                          className="text-gray-400 hover:text-gray-600 text-xs cursor-move"
                          title="ドラッグして並び替え"
                        >
                          ⋮⋮
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Group Details and Videos */}
        <div className="md:col-span-2">
          {selectedGroup ? (
            <div
              className="bg-white rounded-lg border border-gray-200 p-6"
              onDragOver={(e) => {
                if (!draggedVideo) return
                // The whole panel is one drop zone so videos can be dropped
                // above the first card and below the last card too.
                e.preventDefault()
                const items = Array.from(
                  e.currentTarget.querySelectorAll<HTMLElement>('[data-video-item]')
                )
                let insertIndex = items.length
                for (let i = 0; i < items.length; i++) {
                  const rect = items[i].getBoundingClientRect()
                  if (e.clientY < rect.top + rect.height / 2) {
                    insertIndex = i
                    break
                  }
                }
                setDragOverVideoIndex(insertIndex)
              }}
              onDrop={(e) => {
                e.preventDefault()
                if (dragOverVideoIndex !== null) {
                  handleVideoDrop(dragOverVideoIndex)
                } else {
                  setDraggedVideo(null)
                  setDragOverVideoIndex(null)
                }
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">{selectedGroup.name}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAddVideos(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                  >
                    動画を追加
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(selectedGroup.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                  >
                    グループを削除
                  </button>
                </div>
              </div>

              {/* Featured Videos List */}
              <div className="space-y-3">
                {featuredVideos.length > 0 ? (
                  featuredVideos.map((featuredVideo, index) => (
                    <div
                      key={featuredVideo.id}
                      data-video-item
                      draggable
                      onDragStart={() => setDraggedVideo(featuredVideo.id)}
                      onDragEnd={() => {
                        setDraggedVideo(null)
                        setDragOverVideoIndex(null)
                      }}
                      className={`relative flex items-center gap-3 p-3 bg-gray-50 rounded ${
                        draggedVideo === featuredVideo.id ? 'opacity-50' : ''
                      }`}
                    >
                      {/* Drop-position indicator */}
                      {draggedVideo && dragOverVideoIndex === index && (
                        <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                      )}
                      {draggedVideo && index === featuredVideos.length - 1 && dragOverVideoIndex === featuredVideos.length && (
                        <div className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                      )}
                      <span className="text-gray-400 cursor-move select-none" title="ドラッグして並び替え">⋮⋮</span>
                      <div className="flex-shrink-0 w-20 h-12 rounded overflow-hidden">
                        <VideoThumbnail thumbnailUrl={featuredVideo.video?.thumbnail_url} title={featuredVideo.video?.title || ''} className="w-full h-full" />
                      </div>
                      <div className="flex-grow">
                        <p className="text-sm font-medium">{featuredVideo.video?.title}</p>
                        <p className="text-xs text-gray-500">{featuredVideo.video?.duration}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleReorderVideos(featuredVideo.id, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleReorderVideos(featuredVideo.id, 'down')}
                          disabled={index === featuredVideos.length - 1}
                          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => handleRemoveVideo(featuredVideo.id)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">
                    このグループにはまだ動画がありません
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">グループを選択してください</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Group Modal */}
      {showAddGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">新規グループ作成</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  グループ名
                </label>
                <input
                  type="text"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  説明（任意）
                </label>
                <textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  表示順
                </label>
                <input
                  type="number"
                  value={groupForm.display_order}
                  onChange={(e) => setGroupForm({ ...groupForm, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={groupForm.is_active}
                  onChange={(e) => setGroupForm({ ...groupForm, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  有効
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddGroup(false)
                  setGroupForm({ name: '', description: '', display_order: 0, is_active: true })
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                キャンセル
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={!groupForm.name}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
              >
                作成
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Videos Modal */}
      {showAddVideos && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <h3 className="text-lg font-medium mb-4">動画を追加</h3>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="動画を検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex-grow overflow-y-auto">
              <div className="space-y-2">
                {filteredAvailableVideos.map((video) => (
                  <div
                    key={video.id}
                    className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${
                      selectedVideos.includes(video.id)
                        ? 'bg-blue-50 border-blue-200 border'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      if (selectedVideos.includes(video.id)) {
                        setSelectedVideos(selectedVideos.filter(id => id !== video.id))
                      } else {
                        setSelectedVideos([...selectedVideos, video.id])
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedVideos.includes(video.id)}
                      onChange={() => {}}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-shrink-0 w-20 h-12 rounded overflow-hidden">
                      <VideoThumbnail thumbnailUrl={video.thumbnail_url} title={video.title} className="w-full h-full" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-sm font-medium">{video.title}</p>
                      <p className="text-xs text-gray-500">{video.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {selectedVideos.length}個の動画を選択中
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAddVideos(false)
                    setSelectedVideos([])
                    setSearchTerm('')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleAddVideos}
                  disabled={selectedVideos.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  追加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FeaturedVideoManager